import { logger } from '../services/logger';
/**
 * Utilities for storing and retrieving registration confirmation data in browser storage.
 * Scoped to sessionStorage with an authenticated userId binding and a short-lived (1-hour) TTL
 * so users who refresh or return to the confirmation page in their active session see their exact
 * live confirmation details, while shared/public browsers drop or deny access upon sign-out or session end.
 */

export const CONFIRMATION_STORAGE_KEY = 'tas_latest_registration_confirmation';
export const CONFIRMATION_TTL_MS = 60 * 60 * 1000; // 1 hour (Issue #264)

/**
 * Checks if a given timestamp is within the allowable TTL.
 * @param {number|string|Date} timestamp
 * @param {number} [ttlMs=CONFIRMATION_TTL_MS]
 * @returns {boolean}
 */
export function isConfirmationFresh(timestamp, ttlMs = CONFIRMATION_TTL_MS) {
    if (!timestamp) return false;
    const timeMs = typeof timestamp === 'number' ? timestamp : new Date(timestamp).getTime();
    if (isNaN(timeMs)) return false;
    const age = Date.now() - timeMs;
    return age >= 0 && age < ttlMs;
}

/**
 * Persists confirmation data to sessionStorage with a savedAt timestamp and bound userId.
 * Gracefully ignores storage errors (e.g. private browsing or storage quota limits).
 * Also purges any legacy entry from localStorage to prevent cross-session leakage.
 * @param {object} data
 * @param {string} [userId]
 */
export function saveConfirmationData(data, userId) {
    if (!data || typeof data !== 'object') return;
    try {
        const payload = {
            ...data,
            userId: userId || data.userId || null,
            savedAt: Date.now()
        };
        if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.setItem(CONFIRMATION_STORAGE_KEY, JSON.stringify(payload));
        }
        // Purge any lingering legacy key from localStorage
        if (typeof window !== 'undefined' && window.localStorage) {
            try {
                window.localStorage.removeItem(CONFIRMATION_STORAGE_KEY);
            } catch { /* ignore */ }
        }
    } catch (err) {
        logger.warn('Could not persist registration confirmation to storage:', err);
    }
}

const readRawConfirmation = () => {
    if (typeof window === 'undefined') return null;
    let raw = window.sessionStorage ? window.sessionStorage.getItem(CONFIRMATION_STORAGE_KEY) : null;
    if (!raw && window.localStorage) {
        raw = window.localStorage.getItem(CONFIRMATION_STORAGE_KEY);
    }
    return raw;
};

const isConfirmationOwned = (parsed, expectedUserId) => {
    if (expectedUserId === undefined) return true;
    if (!expectedUserId) return false;
    if (parsed.userId && parsed.userId !== expectedUserId) return false;
    return true;
};

/**
 * Retrieves stored confirmation data from sessionStorage if still valid, fresh (< 1 hour),
 * and belonging to the expected user UID (if provided).
 * If data is expired or malformed, clears it from storage and returns null.
 * @param {string} [expectedUserId]
 * @param {number} [ttlMs=CONFIRMATION_TTL_MS]
 * @returns {object|null}
 */
export function getStoredConfirmationData(expectedUserId, ttlMs = CONFIRMATION_TTL_MS) {
    try {
        const raw = readRawConfirmation();
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') {
            clearStoredConfirmationData();
            return null;
        }

        const timestamp = parsed.savedAt || parsed.timestamp;
        if (!isConfirmationFresh(timestamp, ttlMs)) {
            clearStoredConfirmationData();
            return null;
        }

        if (!isConfirmationOwned(parsed, expectedUserId)) {
            return null;
        }

        return parsed;
    } catch (err) {
        logger.warn('Could not read stored registration confirmation:', err);
        clearStoredConfirmationData();
        return null;
    }
}

/**
 * Clears the stored confirmation data from both sessionStorage and localStorage.
 */
export function clearStoredConfirmationData() {
    if (typeof window === 'undefined') return;
    [window.sessionStorage, window.localStorage].forEach((storage) => {
        if (!storage) return;
        try {
            storage.removeItem(CONFIRMATION_STORAGE_KEY);
        } catch (err) {
            logger.warn('Could not remove stored registration confirmation:', err);
        }
    });
}

